import { useCallback, useMemo } from 'react';
import { useBadgeQuery } from '../../../../contexts/BadgeResourceContext';
import { useScopedBadgeState } from '../../../../hooks/useScopedBadgeState';
import { getEventImageTree, getEventMaterialTree } from '../../../../api/eventDetails';

const STORAGE_PREFIX = 'casticart:event-chat-assets-seen:v1';

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

const countTreeItems = (tree, key) => {
  const direct = safeNumber(tree?.[`${key}_count`] ?? tree?.[`${key}Count`]);
  if (direct) return direct;
  const rows = Array.isArray(tree?.[key]) ? tree[key] : [];
  return rows.length;
};

const totalTreeCount = (tree) => safeNumber(tree?.total_count ?? tree?.totalCount);

const listFolders = (tree) => (Array.isArray(tree?.folders) ? tree.folders : []);

const folderIdOf = (folder) => folder?.id ?? folder?.folder_id ?? folder?.folderId ?? null;

const countTreeRecursive = async ({ eventId, fetchTree, itemKey, visited = new Set(), folderId = null, signal }) => {
  const visitKey = folderId == null ? 'root' : String(folderId);
  if (visited.has(visitKey)) return 0;
  visited.add(visitKey);
  signal?.throwIfAborted();
  const tree = await fetchTree(eventId, folderId);
  signal?.throwIfAborted();
  if (!tree || typeof tree !== 'object' || (!Array.isArray(tree[itemKey]) && tree.total_count == null && tree.totalCount == null)) {
    throw new Error('Invalid event asset tree');
  }
  const total = totalTreeCount(tree);
  if (tree.total_count != null || tree.totalCount != null) {
    const rawTotal = Number(tree.total_count ?? tree.totalCount);
    if (!Number.isSafeInteger(rawTotal) || rawTotal < 0) throw new Error('Invalid asset count');
    return total;
  }
  const childFolders = listFolders(tree);
  const childCounts = await Promise.all(
    childFolders
      .map(folderIdOf)
      .filter((id) => id !== null && id !== undefined && id !== '')
      .map((id) => countTreeRecursive({ eventId, fetchTree, itemKey, visited, folderId: id, signal })),
  );
  return countTreeItems(tree, itemKey) + childCounts.reduce((sum, count) => sum + count, 0);
};

const readSeenState = (storageKey) => {
  if (typeof window === 'undefined') return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeSeenState = (storageKey, next) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(next));
  } catch {
    // Ignore storage quota/private mode failures; badges remain best-effort.
  }
};

/** @param {{ eventId?: string | null, viewerKey?: string }} options */
export default function useEventChatAssetBadges({ eventId, viewerKey = 'user' } = {}) {
  const storageKey = useMemo(
    () => `${STORAGE_PREFIX}:${viewerKey}:${eventId || 'none'}`,
    [eventId, viewerKey],
  );
  const [seen, setSeen] = useScopedBadgeState(storageKey, readSeenState(storageKey));
  const materials = useBadgeQuery({
    queryKey: ['event-material-count', viewerKey, eventId],
    enabled: Boolean(eventId), initialValue: 0,
    queryFn: ({ signal }) => countTreeRecursive({ eventId, fetchTree: getEventMaterialTree, itemKey: 'materials', signal }),
  });
  const album = useBadgeQuery({
    queryKey: ['event-album-count', viewerKey, eventId],
    enabled: Boolean(eventId), initialValue: 0,
    queryFn: ({ signal }) => countTreeRecursive({ eventId, fetchTree: getEventImageTree, itemKey: 'images', signal }),
  });
  const counts = useMemo(() => ({ materials: materials.data, album: album.data }), [materials.data, album.data]);
  const refresh = useCallback(() => Promise.all([materials.refresh(), album.refresh()]), [materials.refresh, album.refresh]);

  const markSeen = useCallback((type) => {
    setSeen((current) => {
      const next = {
        ...current,
        [type]: counts[type] || 0,
      };
      writeSeenState(storageKey, next);
      return next;
    });
  }, [counts, storageKey]);

  const buildBadge = useCallback((type) => {
    const count = counts[type] || 0;
    const seenCount = safeNumber(seen[type]);
    return {
      count,
      hasAny: count > 0,
      hasNew: count > seenCount,
      tone: count > seenCount ? 'new' : count > 0 ? 'has' : 'none',
    };
  }, [counts, seen]);

  return {
    materialBadge: buildBadge('materials'),
    albumBadge: buildBadge('album'),
    hasAny: counts.materials > 0 || counts.album > 0,
    hasNew: counts.materials > safeNumber(seen.materials) || counts.album > safeNumber(seen.album),
    markMaterialsSeen: () => markSeen('materials'),
    markAlbumSeen: () => markSeen('album'),
    refresh,
  };
}
